<!-- src/lib/components/FilePreview.svelte -->
<!--
  Inline file preview for the file explorer pane.

  Reads `path` via GET /api/v1/files/read and renders it in a scrollable
  <pre> with line numbers. No syntax highlighting yet — the backend
  already returns text/plain and we stay dependency-free. A full
  highlighter (shiki, highlight.js, codemirror) is a later pass when we
  decide the bundle cost is worth it.

  Error states the backend can return:
    413 Payload Too Large     — "File too big (2MB cap)"
    415 Unsupported Media Type — "Binary file not shown"
    400 Bad Request            — path validation failed
    500 Internal Server Error  — fs read error

  Each state renders a distinct message so the user knows why they're
  not seeing content.
-->
<script lang="ts">
  import { apiFetch } from '../api.ts';

  interface Props {
    path: string | null;
  }
  let { path }: Props = $props();

  type LoadState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'text'; content: string }
    | { kind: 'too_big' }
    | { kind: 'binary' }
    | { kind: 'error'; message: string };

  let state = $state<LoadState>({ kind: 'idle' });

  $effect(() => {
    if (!path) {
      state = { kind: 'idle' };
      return;
    }
    void loadFile(path);
  });

  async function loadFile(p: string) {
    state = { kind: 'loading' };
    try {
      const res = await apiFetch(`/api/v1/files/read?path=${encodeURIComponent(p)}`);
      if (res.ok) {
        state = { kind: 'text', content: await res.text() };
        return;
      }
      if (res.status === 413) {
        state = { kind: 'too_big' };
        return;
      }
      if (res.status === 415) {
        state = { kind: 'binary' };
        return;
      }
      state = { kind: 'error', message: `HTTP ${res.status}: ${await res.text()}` };
    } catch (e) {
      state = {
        kind: 'error',
        message: e instanceof Error ? e.message : 'Failed to fetch file',
      };
    }
  }

  // Split the content into lines once so the template can render line
  // numbers without recomputing on every reactive read.
  const lines: string[] = $derived(
    state.kind === 'text' ? state.content.split('\n') : []
  );

  // Truncate path for the header — keep the filename visible
  function filename(p: string): string {
    const ix = p.lastIndexOf('/');
    return ix >= 0 ? p.slice(ix + 1) : p;
  }

  function shortDir(p: string): string {
    const ix = p.lastIndexOf('/');
    if (ix < 0) return '';
    const dir = p.slice(0, ix);
    // Tilde-collapse $HOME on macOS/Linux
    const m = dir.match(/^\/Users\/([^/]+)(\/.*)?$/);
    if (m) return '~' + (m[2] ?? '');
    return dir;
  }
</script>

<div class="file-preview">
  <div class="preview-header">
    <span class="header-label">Preview</span>
    {#if path}
      <span class="preview-filename" title={path}>{filename(path)}</span>
    {/if}
  </div>

  {#if path}
    <div class="preview-path" title={path}>{shortDir(path)}</div>
  {/if}

  <div class="preview-body">
    {#if state.kind === 'idle'}
      <div class="preview-empty">
        <p>No file selected.</p>
        <p class="empty-hint">Click a file in the tree to preview it here.</p>
      </div>
    {:else if state.kind === 'loading'}
      <div class="preview-empty">
        <p>Loading…</p>
      </div>
    {:else if state.kind === 'too_big'}
      <div class="preview-empty error">
        <p>File is too large to preview.</p>
        <p class="empty-hint">2 MB cap. Open it in your editor instead.</p>
      </div>
    {:else if state.kind === 'binary'}
      <div class="preview-empty error">
        <p>Binary file not shown.</p>
        <p class="empty-hint">This file isn't valid UTF-8.</p>
      </div>
    {:else if state.kind === 'error'}
      <div class="preview-empty error">
        <p>Couldn't read the file.</p>
        <p class="empty-hint">{state.message}</p>
      </div>
    {:else if state.kind === 'text'}
      <pre class="code"><table class="code-table"><tbody>
        {#each lines as line, i}
          <tr class="code-row"><td class="line-num">{i + 1}</td><td class="line-content">{line}</td></tr>
        {/each}
      </tbody></table></pre>
    {/if}
  </div>
</div>

<style>
  .file-preview {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: oklch(0.82 0.04 310 / 0.88);
    font-size: 0.78rem;
    overflow: hidden;
  }

  .preview-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.55rem 0.75rem 0.4rem 0.75rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.12);
  }
  .header-label {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: oklch(0.72 0.08 310 / 0.6);
    flex-shrink: 0;
  }
  .preview-filename {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    color: oklch(0.92 0.05 310);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .preview-path {
    flex-shrink: 0;
    padding: 0.3rem 0.75rem 0.4rem;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.65rem;
    color: oklch(0.55 0.04 310 / 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.08);
  }

  .preview-body {
    flex: 1;
    overflow: auto;
    min-height: 0;
  }
  .preview-body::-webkit-scrollbar { width: 8px; height: 8px; }
  .preview-body::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.4);
    border-radius: 4px;
  }
  .preview-body::-webkit-scrollbar-thumb:hover {
    background: oklch(0.40 0.10 310 / 0.5);
  }

  .preview-empty {
    padding: 1.75rem 1rem;
    text-align: center;
    color: oklch(0.55 0.04 310 / 0.55);
    font-size: 0.78rem;
  }
  .preview-empty p {
    margin: 0 0 0.35rem 0;
  }
  .preview-empty .empty-hint {
    font-size: 0.7rem;
    color: oklch(0.50 0.03 310 / 0.45);
    word-break: break-word;
  }
  .preview-empty.error p:first-child {
    color: oklch(0.72 0.22 25 / 0.85);
  }

  /* ── Code render ────────────────────────────────────────────────── */
  .code {
    margin: 0;
    padding: 0;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    line-height: 1.55;
    color: oklch(0.88 0.03 310);
    tab-size: 4;
    white-space: pre;
  }
  .code-table {
    width: max-content;
    border-collapse: collapse;
    border-spacing: 0;
  }
  .code-row {
    /* contain the row's paint so scrolling the pane doesn't re-raster
       the whole viewport — important for long files */
    contain: layout paint;
  }
  .line-num {
    padding: 0 0.6rem 0 0.75rem;
    color: oklch(0.45 0.03 310 / 0.55);
    text-align: right;
    user-select: none;
    white-space: nowrap;
    border-right: 1px solid oklch(0.30 0.05 275 / 0.18);
    font-variant-numeric: tabular-nums;
  }
  .line-content {
    padding: 0 0.75rem;
    white-space: pre;
  }
  .code-row:hover .line-num {
    color: oklch(0.72 0.10 310 / 0.8);
  }
</style>
